import { ArrowDown2, ArrowRight2 } from 'iconsax-reactjs';
import { type FC, useState } from 'react';
import { Link } from '@/base/router';
import { Button } from '@/shared/Button';
import { PopoverContent, PopoverRoot, PopoverTrigger } from '@/shared/Popover';
import { cn } from '@/shared/react';
import type { HeaderLink } from './useHeaderLinks';

type Props = {
  item: HeaderLink;
  mode: 'desktop' | 'mobile';
  onNavigate?: () => void;
};

const isExternal = (href?: string) => !!href?.startsWith('http');

export const HeaderNavLink: FC<Props> = ({ item, mode, onNavigate }) => {
  if (item.hidden) return null;
  if (item.children)
    return mode === 'desktop' ? (
      <DesktopGroup item={item} />
    ) : (
      <MobileGroup item={item} onNavigate={onNavigate} />
    );
  return mode === 'desktop' ? (
    <DesktopLink item={item} />
  ) : (
    <MobileLink item={item} onNavigate={onNavigate} />
  );
};

const DesktopLink: FC<{
  item: HeaderLink;
  size?: 8 | 10;
  align?: 'start' | 'center';
  onNavigate?: () => void;
}> = ({ item, size = 8, align = 'center', onNavigate }) => {
  const path = Array.isArray(item.link) ? item.link[0] : item.link;

  const button = (
    <Button
      variant="transparent"
      size={size}
      className={cn(
        'shrink-0',
        align === 'start' ? '!justify-start !rounded-lg' : undefined,
      )}
      onClick={
        !item.link
          ? () => {
              item.onClick?.();
              onNavigate?.();
            }
          : undefined
      }
    >
      {item.label}
      {item.badge}
    </Button>
  );
  if (isExternal(path))
    return (
      <a
        href={path}
        className="contents"
        target="_blank"
        rel="noopener noreferrer"
        onClick={onNavigate}
      >
        {button}
      </a>
    );
  if (path)
    return (
      <Link
        workspace={Array.isArray(item.link) ? item.link[1] : undefined}
        to={path}
        className="contents"
        onClick={onNavigate}
      >
        {button}
      </Link>
    );
  return button;
};

const MobileLink: FC<{ item: HeaderLink; onNavigate?: () => void }> = ({
  item,
  onNavigate,
}) => {
  const path = Array.isArray(item.link) ? item.link[0] : item.link;
  const cls = 'flex gap-2 justify-between items-center shrink-0 h-8 w-full';
  const content = (
    <>
      {item.label}
      {item.badge ?? <ArrowRight2 size={18} className="auto-rtl" />}
    </>
  );
  if (isExternal(path))
    return (
      <a
        href={path}
        className={cls}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onNavigate}
      >
        {content}
      </a>
    );
  if (path)
    return (
      <Link
        workspace={Array.isArray(item.link) ? item.link[1] : undefined}
        to={path}
        className={cls}
        onClick={onNavigate}
      >
        {content}
      </Link>
    );
  return (
    <button
      type="button"
      className={cls}
      onClick={() => {
        item.onClick?.();
        onNavigate?.();
      }}
    >
      {content}
    </button>
  );
};

const MoreButton: FC<{
  item: HeaderLink;
  onNavigate?: () => void;
  className?: string;
}> = ({ item, onNavigate, className }) => {
  const path = Array.isArray(item.link) ? item.link[0] : item.link;
  const handleClick = () => {
    item.onClick?.();
    onNavigate?.();
  };
  if (path) {
    return (
      <Link
        workspace={Array.isArray(item.link) ? item.link[1] : undefined}
        to={path}
        className="contents"
        onClick={handleClick}
      >
        <Button variant="primary" block size={10} className={className}>
          {item.more}
        </Button>
      </Link>
    );
  }
  return (
    <Button
      variant="primary"
      block
      size={10}
      className={className}
      onClick={handleClick}
    >
      {item.more}
    </Button>
  );
};

const DesktopGroup: FC<{ item: HeaderLink }> = ({ item }) => {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  return (
    <PopoverRoot open={open} onOpenChange={setOpen}>
      <PopoverTrigger openOnHover>
        <Button variant="transparent" type="div" size={8} className="gap-1">
          {item.label} <ArrowDown2 size={16} />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        className={cn('p-1 min-w-40 relative', item.loading && 'min-h-64')}
        initialFocus={false}
      >
        {item.loading && (
          <div className="absolute inset-0 shimmer pointer-events-none" />
        )}
        <div className="flex flex-col gap-0 max-h-64 overflow-auto">
          {item.children
            ?.filter((c) => !c.hidden)
            .map((child) => (
              <DesktopLink
                key={child.key}
                item={child}
                size={10}
                align="start"
                onNavigate={close}
              />
            ))}
        </div>
        {item.more && (
          <MoreButton item={item} onNavigate={close} className="mt-3 w-full" />
        )}
      </PopoverContent>
    </PopoverRoot>
  );
};

const MobileGroup: FC<{
  item: HeaderLink;
  onNavigate?: () => void;
}> = ({ item, onNavigate }) => {
  const [open, setOpen] = useState(false);
  const panelId = `header-group-${item.key}`;
  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        className="flex gap-2 justify-between items-center w-full h-8"
        onClick={() => setOpen((prev) => !prev)}
      >
        <span>{item.label}</span>
        <ArrowRight2
          size={18}
          className={cn('transition-transform', open && 'rotate-90')}
        />
      </button>
      <div
        id={panelId}
        className={cn(
          'overflow-hidden transition-[max-height,opacity] duration-300 ease-in-out relative',
          open
            ? 'max-h-auto opacity-100 mt-3'
            : 'max-h-0 opacity-0 pointer-events-none',
        )}
      >
        <div className="flex flex-col gap-3 bg-background-hover/20 p-3 rounded-lg">
          {item.children?.map((child) => (
            <HeaderNavLink
              key={child.key}
              item={child}
              mode="mobile"
              onNavigate={onNavigate}
            />
          ))}
        </div>
        {item.more && (
          <MoreButton
            item={item}
            onNavigate={onNavigate}
            className="mt-3 w-full"
          />
        )}
      </div>
    </div>
  );
};
